import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import moment from "moment";
import supabase from "../../../supabase";
import { LoaderSpinner } from "../../../components/spinner/loader";
import DataTable from "../../../components/ui/DataTable";
import RowAction, { RowActions } from "../../../components/ui/RowAction";
import { Num } from "../../../components/ui";
import { IconEye } from "../../../components/ui/icons";
import { tokens } from "../../../theme";
import { ticketObjectStatus } from "../../../constants/ticketStatus";
import { formatCurrencyManual } from "../../../utils/formatters";
import { useNavigate } from "react-router-dom";

/** Tramos de antigüedad, en días desde la fecha de la factura. */
const BUCKETS = [
  { id: "b0", label: "0 a 30", max: 30, tone: "var(--sup-state-ok-ink)", bg: "var(--sup-state-ok-bg)" },
  { id: "b30", label: "31 a 60", max: 60, tone: "var(--sup-state-warn-ink)", bg: "var(--sup-state-warn-bg)" },
  { id: "b60", label: "61 a 90", max: 90, tone: "var(--sup-state-info-ink)", bg: "var(--sup-state-info-bg)" },
  { id: "b90", label: "más de 90", max: Infinity, tone: "var(--sup-state-danger-ink)", bg: "var(--sup-state-danger-bg)" },
];

const ticketTotal = (ticket) =>
  (ticket.ticketDetails || []).reduce((acc, detail) => {
    const subtotal = (parseFloat(detail.price) || 0) * (parseFloat(detail.quantity) || 0);
    return acc + subtotal + subtotal * ((parseFloat(detail.ivaPercentage) || 0) / 100);
  }, 0);

/**
 * Antigüedad de la deuda.
 *
 * Toma las facturas autorizadas sin pagar (tickets.status = 2) y las reparte
 * por tramos según cuántos días pasaron desde su emisión. Es el reporte que
 * responde a "¿a quién hay que ir a cobrarle primero?".
 */
const DebtAging = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [bucketFilter, setBucketFilter] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from("tickets")
        .select(
          "ticketId, ticketNumber, ticketDate, status, client: clientId(clientId, company, cuit), ticketDetails(price, quantity, ivaPercentage)"
        )
        .eq("status", ticketObjectStatus.autorizada);

      if (err) throw new Error(err.message);

      const byClient = new Map();

      (data || []).forEach((ticket) => {
        const client = ticket.client;
        if (!client) return;

        const total = ticketTotal(ticket);
        if (!total) return;

        const days = moment().diff(moment(ticket.ticketDate), "days");
        const bucket = BUCKETS.find((b) => days <= b.max) || BUCKETS[BUCKETS.length - 1];

        const current =
          byClient.get(client.clientId) ||
          {
            clientId: client.clientId,
            company: client.company || "Sin razón social",
            cuit: client.cuit || "",
            facturas: 0,
            oldest: 0,
            total: 0,
            b0: 0,
            b30: 0,
            b60: 0,
            b90: 0,
          };

        current.facturas += 1;
        current.total += total;
        current[bucket.id] += total;
        current.oldest = Math.max(current.oldest, days);
        byClient.set(client.clientId, current);
      });

      setRows([...byClient.values()].sort((a, b) => b.total - a.total));
    } catch (e) {
      setError(e.message || "No se pudo armar el reporte");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const term = search.trim().toLowerCase();

  const visibleRows = useMemo(
    () =>
      rows.filter((row) => {
        if (bucketFilter && !(row[bucketFilter] > 0)) return false;
        if (!term) return true;
        return [row.company, row.cuit]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      }),
    [rows, term, bucketFilter]
  );

  const totals = useMemo(() => {
    const acc = { total: 0, b0: 0, b30: 0, b60: 0, b90: 0 };
    rows.forEach((row) => {
      acc.total += row.total;
      BUCKETS.forEach((b) => {
        acc[b.id] += row[b.id];
      });
    });
    return acc;
  }, [rows]);

  const amountColumn = (id) => ({
    name: id,
    label: BUCKETS.find((b) => b.id === id).label,
    options: {
      filter: false,
      sort: true,
      setCellProps: () => ({ style: { width: 128, textAlign: "right", whiteSpace: "nowrap" } }),
      setCellHeaderProps: () => ({ style: { width: 128, textAlign: "right" } }),
      customBodyRender: (value) =>
        value > 0 ? (
          <Num sx={{ fontSize: "13px", color: tokens.ink }}>{formatCurrencyManual(value)}</Num>
        ) : (
          <Num sx={{ fontSize: "13px", color: tokens.ink4 }}>—</Num>
        ),
    },
  });

  const columns = [
    {
      name: "company",
      label: "Cliente",
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = visibleRows[dataIndex];
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
              <Typography sx={{ fontSize: "13.5px", fontWeight: 500 }}>{row.company}</Typography>
              <Typography sx={{ fontSize: "11.5px", color: tokens.ink3, fontVariantNumeric: "tabular-nums" }}>
                {row.cuit || "Sin CUIT"} · {row.facturas}{" "}
                {row.facturas === 1 ? "factura" : "facturas"} · más vieja {row.oldest} d
              </Typography>
            </Box>
          );
        },
      },
    },
    ...BUCKETS.map((b) => amountColumn(b.id)),
    {
      name: "total",
      label: "Total adeudado",
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ style: { width: 148, textAlign: "right", whiteSpace: "nowrap" } }),
        setCellHeaderProps: () => ({ style: { width: 148, textAlign: "right" } }),
        customBodyRender: (value) => (
          <Num sx={{ fontSize: "13.5px", fontWeight: 600 }}>{formatCurrencyManual(value)}</Num>
        ),
      },
    },
    {
      name: "acciones",
      label: "Acciones",
      options: {
        filter: false,
        sort: false,
        setCellProps: () => ({ style: { width: 64, textAlign: "right" } }),
        setCellHeaderProps: () => ({ style: { width: 64, textAlign: "right" } }),
        customBodyRenderLite: (dataIndex) => {
          const row = visibleRows[dataIndex];
          return (
            <RowActions>
              <RowAction
                icon={IconEye}
                title="Ver cuenta corriente"
                onClick={() =>
                  navigate("/client-current-account", {
                    state: { client: { clientId: row.clientId, company: row.company, cuit: row.cuit } },
                  })
                }
              />
            </RowActions>
          );
        },
      },
    },
  ];

  const chips = [
    {
      id: "todos",
      label: "Todos",
      active: bucketFilter === null,
      onClick: () => setBucketFilter(null),
    },
    ...BUCKETS.map((b) => ({
      id: b.id,
      label: b.label,
      dot: b.tone,
      active: bucketFilter === b.id,
      onClick: () => setBucketFilter(bucketFilter === b.id ? null : b.id),
    })),
  ];

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
        <LoaderSpinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, paddingY: 6 }}>
        <Typography sx={{ color: tokens.ink2 }}>{error}</Typography>
        <Button variant="outlined" onClick={load}>
          Reintentar
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(168px, 1fr))",
          gap: "10px",
        }}
      >
        {BUCKETS.map((b) => (
          <Box
            key={b.id}
            sx={{
              backgroundColor: tokens.surface,
              border: `1px solid ${tokens.border}`,
              borderRadius: `${tokens.radiusCard}px`,
              padding: "13px 15px",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              minWidth: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
              <Box sx={{ width: 8, height: 8, borderRadius: 999, backgroundColor: b.tone }} />
              <Typography sx={{ fontSize: "11.5px", fontWeight: 500, color: tokens.ink2 }}>
                {b.label} días
              </Typography>
            </Box>
            <Num sx={{ fontSize: "19px", fontWeight: 600, lineHeight: 1.1 }}>
              {formatCurrencyManual(totals[b.id])}
            </Num>
            <Typography sx={{ fontSize: "11.5px", color: tokens.ink3 }}>
              {totals.total > 0 ? `${Math.round((totals[b.id] / totals.total) * 100)}% del total` : "Sin deuda"}
            </Typography>
          </Box>
        ))}
      </Box>

      <DataTable
        data={visibleRows}
        columns={columns}
        heading="Antigüedad de la deuda"
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Buscar por cliente o CUIT…",
        }}
        chips={chips}
        actions={
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "baseline", gap: "8px" }}>
            <Typography sx={{ fontSize: "12px", color: tokens.ink3 }}>Deuda total</Typography>
            <Num sx={{ fontSize: "15px", fontWeight: 600, color: tokens.brandDark }}>
              {formatCurrencyManual(totals.total)}
            </Num>
          </Box>
        }
        resultCount={visibleRows.length}
        totalCount={rows.length}
        emptyMessage="No hay facturas autorizadas pendientes de pago"
        options={{
          sortOrder: { name: "total", direction: "desc" },
          rowsPerPage: 25,
          downloadOptions: { filename: "antiguedad-de-deuda.csv", separator: ";" },
        }}
      />
    </Box>
  );
};

export default DebtAging;
